import { Settings } from '@/app'
import { useLinkedIn } from 'react-linkedin-login-oauth2'

import { getRedirectUri } from '@/features/Auth/session/linkedInRedirect'
import { getLinkedInAccessToken } from '@/features/Auth/session/views'

/**
 * LinkedIn's popup, which produces the code every other provider does not need.
 *
 * It has to be a hook — `useLinkedIn` owns the popup and its callback — so this is where the
 * one provider that cannot be started by a plain function call is started. What it hands
 * back is an access token, and from there LinkedIn is a provider like any other: nothing
 * downstream of `signInWith('linkedin', …)` knows it took a popup to get here. See EPIC-005.
 */
export function useLinkedInSignIn(signInWith: (provider: 'linkedin', input: { accessToken: string }) => void) {
  const { linkedInLogin } = useLinkedIn({
    clientId: Settings.ApiCredentials.LinkedIn.ClientID,
    redirectUri: getRedirectUri(),
    scope: 'r_liteprofile r_emailaddress',
    async onSuccess(code: string) {
      try {
        const tokenData = await getLinkedInAccessToken(code)

        // Null means the code was already spent — the callback route rendered twice. The
        // sign-in it belongs to is already running; starting a second one would race it.
        if (!tokenData?.access_token) return

        signInWith('linkedin', { accessToken: tokenData.access_token })
      } catch (error) {
        logger.error('Could not exchange the LinkedIn code', error, 'Auth')
      }
    },
    onError(error: unknown) {
      // Closing LinkedIn's popup arrives here, so this is not reported: it is someone backing
      // out, and the machine never left `anonymous`.
      logger.info('LinkedIn sign-in did not complete', error, 'Auth')
    }
  })

  return linkedInLogin
}
